import { useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { searchKnowledge, type SearchResponse } from "../api/knowledge.ts";

const SCOPES = ["", "company", "client", "project", "product", "technical", "meeting", "contract", "marketing"];

export function KnowledgeSearchPage() {
  const { token } = useAuth();
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("");
  const [topK, setTopK] = useState(10);
  const [minScore, setMinScore] = useState(0.5);
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim() || token == null) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchKnowledge(token, query.trim(), { topK, minScore, scope: scope || undefined });
      setResults(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Search failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">Knowledge Search</h1>

        <form onSubmit={handleSearch} className="space-y-4">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Search knowledge base…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
            />
            <button
              type="submit"
              disabled={loading || !query.trim()}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 transition-colors"
            >
              {loading ? "Searching…" : "Search"}
            </button>
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2 text-gray-400">
              Scope:
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                className="px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none"
              >
                {SCOPES.map((s) => <option key={s} value={s}>{s || "All"}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-gray-400">
              Top K:
              <input
                type="number"
                min={1}
                max={20}
                value={topK}
                onChange={(e) => setTopK(parseInt(e.target.value) || 10)}
                className="w-16 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none"
              />
            </label>
            <label className="flex items-center gap-2 text-gray-400">
              Min Score:
              <input
                type="number"
                min={0}
                max={1}
                step={0.1}
                value={minScore}
                onChange={(e) => setMinScore(parseFloat(e.target.value) || 0.5)}
                className="w-20 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-white focus:outline-none"
              />
            </label>
          </div>
        </form>

        {error != null && <p className="text-red-400">{error}</p>}

        {results != null && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-400">
                {results.totalFound} result{results.totalFound !== 1 ? "s" : ""} in {results.durationMs}ms
              </p>
            </div>

            {results.results.length === 0 ? (
              <div className="rounded-lg border border-gray-800 p-8 text-center text-gray-500">
                No results found. Try different search terms or a lower minimum score.
              </div>
            ) : (
              <div className="space-y-3">
                {results.results.map((r, i) => (
                  <div key={r.chunkId} className="rounded-lg border border-gray-800 p-4 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <span className="text-xs text-gray-500 mr-2">#{i + 1}</span>
                        <span className="text-white font-medium">{r.documentTitle}</span>
                        {r.heading != null && (
                          <span className="text-gray-400 ml-2 text-sm">› {r.heading}</span>
                        )}
                      </div>
                      <span className="text-xs text-green-400 font-mono shrink-0">{(r.score * 100).toFixed(1)}%</span>
                    </div>
                    <p className="text-sm text-gray-300 leading-relaxed line-clamp-5">{r.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {results == null && !loading && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-lg mb-2">Ask the knowledge base a question</p>
            <p className="text-sm">Use natural language to find relevant documents, policies, and technical knowledge.</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
